// Small shared helpers.

// Abre un blob (típicamente un PDF) en pestaña nueva de forma compatible con
// iOS/iPadOS Safari. Safari exige que window.open() se llame de forma
// SÍNCRONA dentro del gesto del usuario (el clic); si primero se hace un
// `await` (p.ej. api.get(...) para traer el PDF) y LUEGO se llama
// window.open(), Safari ya no lo asocia al clic y lo bloquea como pop-up —
// en silencio, sin error capturable. Por eso se abre la pestaña ANTES del
// await (con about:blank) y se le asigna la URL del blob cuando ya está listo.
// `cargar` es una función async que debe devolver el Blob.
export async function abrirPdfEnNuevaPestana(cargar, { onError } = {}) {
  const tab = window.open("", "_blank");
  try {
    const blob = await cargar();
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.open(url, "_blank");   // pop-ups bloqueados a nivel navegador: mejor esfuerzo
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    tab?.close();
    onError?.(e);
  }
}

// Ejecuta `accion` (que dispara un setState — agregar/quitar una línea de una
// lista larga) preservando la posición de scroll. El fix anterior
// (`overflow-anchor: none` en index.css) NO sirve en Safari/iOS/iPadOS: esa
// propiedad CSS nunca la implementó WebKit, así que ahí el salto seguía
// pasando pese al fix — de ahí que el usuario lo siguiera reportando. Esto
// es JS puro, funciona en cualquier navegador: guarda scrollTop ANTES del
// cambio y lo reimpone en el frame siguiente (dos rAF por seguridad, para
// cubrir navegadores que tardan un frame extra en reflow-ear listas largas).
export function preservarScroll(accion) {
  const el = document.scrollingElement || document.documentElement;
  const y = el.scrollTop;
  const resultado = accion();
  const restaurar = () => { el.scrollTop = y; };
  requestAnimationFrame(() => { restaurar(); requestAnimationFrame(restaurar); });
  return resultado;
}

// Today's date as "YYYY-MM-DD" in the browser's LOCAL timezone (not UTC).
// `new Date().toISOString()` returns UTC, which in negative-offset zones like
// Venezuela (UTC-4) already rolls over to "tomorrow" after ~8pm — that made
// progress entries land on the wrong day. Build it from local components.
export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// "YYYY-MM-DD" desde hoy más N días (útil para sugerir fechas de entrega por
// defecto: crear una tarea/proyecto ya trae fecha inicio = hoy y compromiso
// a 7 días para que no queden vacíos).
export function todayPlusLocal(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// IDs de un departamento + todos sus descendientes (jerarquía por parent_id).
// Se usa para que los filtros de persona (Responsable / Creado por / PMO)
// muestren solo la gente del departamento elegido, incluyendo sub-equipos.
export function deptWithChildren(departments, deptId) {
  const id = Number(deptId);
  if (!id) return null;
  const ids = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const d of departments) {
      if (d.parent_id && ids.has(Number(d.parent_id)) && !ids.has(d.id)) {
        ids.add(d.id);
        grew = true;
      }
    }
  }
  return ids;
}

// Filtra la lista de usuarios al departamento (y sub-departamentos) elegido.
// Sin departamento seleccionado devuelve todos.
export function usersOfDept(users, departments, deptId) {
  const ids = deptWithChildren(departments, deptId);
  if (!ids) return users;
  return users.filter((u) => ids.has(Number(u.department_id)));
}

// Lista PLANA de departamentos ORDENADA y ANIDADA por jerarquía (padre → hijos),
// con `depth` para sangrar en los <select>. DFS desde las raíces (sin parent, o
// cuyo parent no está en el alcance), hijos ordenados por nombre. Úsala en TODO
// desplegable de departamento (pedido 2026-10-08) vía el componente <DeptOptions>.
export function deptTreeOrdered(departments) {
  const list = (departments || []).filter(Boolean);
  const ids = new Set(list.map((d) => d.id));
  const byParent = new Map();
  for (const d of list) {
    const pid = d.parent_id && ids.has(Number(d.parent_id)) ? Number(d.parent_id) : 0;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(d);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const out = [];
  const walk = (pid, depth) => {
    for (const d of (byParent.get(pid) || [])) {
      out.push({ id: d.id, name: d.name, depth, parent_id: d.parent_id || null });
      walk(d.id, depth + 1);
    }
  };
  walk(0, 0);
  if (out.length < list.length) {   // huérfanos/ciclos: al final, sin sangría
    const seen = new Set(out.map((x) => x.id));
    for (const d of list) if (!seen.has(d.id)) out.push({ id: d.id, name: d.name, depth: 0 });
  }
  return out;
}

// Parse a date value WITHOUT timezone shift. Strings like "YYYY-MM-DD" and
// "YYYY-MM-DDTHH:MM:SS" are interpreted as local, not UTC midnight (which
// would show the wrong day in negative-offset timezones like Panamá UTC-5).
export function parseLocal(d) {
  if (d instanceof Date) return d;
  if (typeof d === "string") {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
  }
  return new Date(d);
}

export function isOverdue(task) {
  if (!task.due_date || task.status === "completed") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parseLocal(task.due_date) < today;
}

export function fmtDate(d) {
  if (!d) return "—";
  // Formato unificado app-wide: "10 nov 26" (día / mes-corto / año 2 dígitos). Pedido 2026-09-20.
  return parseLocal(d).toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "2-digit" });
}

export function fmtDateTime(d) {
  if (!d) return "—";
  return parseLocal(d).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Server timestamps (created_at / updated_at) are stored as NAIVE UTC by SQLite
// (func.now() = CURRENT_TIMESTAMP). They must be read as UTC and converted to
// local time; otherwise a task created 22/06 20:00 (UTC-5) shows 23/06.
export function parseUTC(d) {
  if (d instanceof Date) return d;
  const s = String(d);
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTz ? s : s.replace(" ", "T") + "Z");
}

// Zona horaria del lector para mostrar los timestamps del servidor. La fija
// `applyUserTimezone(tz)` al cargar/actualizar el usuario; por defecto usa la
// del navegador. Multi-país: cada usuario ve las horas en SU zona.
let _userTz = undefined;
let _appTz = undefined;   // zona del AMBIENTE (settings.APP_TZ): prod -5, staging -4
export function applyUserTimezone(tz) { _userTz = tz || undefined; }
// La zona del ambiente TIENE PRIORIDAD para que todas las horas se muestren en
// el mismo offset por entorno (producción UTC-5, staging UTC-4).
export function applyAppTimezone(tz) { _appTz = tz || undefined; }
// Zona por DEFECTO = Venezuela (America/Caracas, UTC-4). Antes, si aún no había
// cargado la zona del ambiente (`/auth/maintenance`) ni la del usuario, se caía a
// la zona del NAVEGADOR — y un navegador/PC en UTC mostraba las horas +4h
// (00:00 Caracas se veía 04:00). Regla del negocio: SIEMPRE Venezuela salvo que la
// config (APP_TZ) o el perfil del usuario diga otra cosa. Pedido 2026-09-28.
const _DEFAULT_TZ = "America/Caracas";
const _tzOpt = () => ({ timeZone: _appTz || _userTz || _DEFAULT_TZ });

// Server-timestamp date only (e.g. "Creada: 22 jun 2026").
export function fmtStampDate(d) {
  if (!d) return "—";
  return parseUTC(d).toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "2-digit", ..._tzOpt() });
}

// Server-timestamp with time (chat, activity, notifications) en la zona del lector.
export function fmtStamp(d) {
  if (!d) return "—";
  return parseUTC(d).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", ..._tzOpt() });
}

// Server-timestamp COMPLETO (día mes año + hora:min) en la zona configurada.
// Para usar en vez de `new Date(x).toLocaleString(...)`, que ignora la zona del
// ambiente y muestra la hora del navegador (UTC en algunos PCs). Pedido 2026-09-28.
export function fmtStampFull(d) {
  if (!d) return "—";
  return parseUTC(d).toLocaleString("es-VE", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", ..._tzOpt() });
}

export function daysBetween(a, b) {
  return Math.round((parseLocal(b) - parseLocal(a)) / 86400000);
}

const STATUS_ES = { not_started: "No iniciado", in_progress: "En progreso", blocked: "Bloqueado", completed: "Completado", postponed: "Pospuesto" };
const PRIORITY_ES = { critical: "Crítica", high: "Alta", medium: "Media", low: "Baja" };

// Client-side CSV export of a task list (works for any user, opens in Excel / other tools).
export function exportTasksCsv(tasks, filename = "mis_tareas.csv") {
  const headers = ["Código", "Título", "Estado", "Prioridad", "% Avance", "Fecha Inicio", "Fecha Compromiso", "Responsables", "Horas", "Tipo"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  tasks.forEach((t) => {
    lines.push([
      t.code || `#${t.id}`, t.title, STATUS_ES[t.status] || t.status, PRIORITY_ES[t.priority] || t.priority,
      t.progress_pct ?? 0, t.start_date || "", t.due_date || "",
      (t.assignees || []).map((a) => a.full_name).join(" / "), t.hours_spent ?? 0, t.task_type || "",
    ].map(esc).join(","));
  });
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Comprime una foto de celular (varios MB, 3000-4000px) a un JPEG liviano
// ANTES de subirla — pedido 2026-08-14: las fotos de smartphone tardan mucho
// en subir y la carga se cae, sobre todo desde el enlace público "Subir al
// celular" (cámara con conexión móvil lenta). Reescala al lado más largo y
// recomprime; si el archivo ya es chico, no es imagen, o algo falla, sube el
// original tal cual (nunca bloquea la subida por esto).
export async function comprimirImagenSiAplica(file, { maxDim = 1920, calidad = 0.82 } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.type === "image/svg+xml" || file.type === "image/gif") return file;   // vectorial / animado: no tocar
  if (file.size < 400 * 1024) return file;   // ya es chica, no vale la pena
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const { width, height } = bitmap;
    if (width <= maxDim && height <= maxDim) { bitmap.close?.(); return file; }
    const scale = Math.min(maxDim / width, maxDim / height);
    const w = Math.round(width * scale), h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", calidad));
    if (!blob || blob.size >= file.size) return file;   // no mejoró, usar el original
    const nombre = file.name.replace(/\.\w+$/, "") + ".jpg";
    return new File([blob], nombre, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;   // cualquier falla (navegador viejo, formato raro): subir el original
  }
}

// Download a small import template the user can fill in.
export function downloadImportTemplate() {
  const blob = new Blob(["﻿" + "Titulo,Descripcion,Prioridad,Estado,Fecha Inicio,Fecha Compromiso,Tipo\r\nEjemplo de tarea,Detalle opcional,Alta,No iniciado,2026-06-20,2026-06-30,operativa\r\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "plantilla_importacion.csv"; a.click();
  URL.revokeObjectURL(url);
}
